<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <!-- Toolbar -->
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>View Downtime Report</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" small text @click="printReport(downtimeReportId)">
          <v-icon left>mdi-printer</v-icon> Print
        </v-btn>
        <v-btn color="white" small text :to="'/downtime-reports'">
          <v-icon left>mdi-arrow-left</v-icon> Back
        </v-btn>
      </v-toolbar>

      <!-- Body -->
      <v-card-text>
        <v-row dense>
          <!-- Basic Info -->
          <v-col cols="12" md="6">
            <strong>Customer:</strong>
            <div>{{ report.customer?.name || 'N/A' }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Unit:</strong>
            <div>{{ report.unit_id || 'N/A' }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <strong>Subject:</strong>
            <div>{{ report.subject || 'N/A' }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Reported Issue:</strong>
            <div>{{ report.reported_issue || 'N/A' }}</div>
          </v-col>

          <v-col cols="12">
            <strong>Description:</strong>
            <div>{{ report.description || 'N/A' }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <strong>Machine Type:</strong>
            <div>{{ report.machine_type?.name || 'N/A' }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <strong>Reported At:</strong>
            <div>{{ formatDate(report.reported_at) }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <strong>Date Start:</strong>
            <div>{{ formatDate(report.date_start) }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <strong>Date End:</strong>
            <div>{{ formatDate(report.date_end) }}</div>
          </v-col>

          <v-col cols="12" md="6">
            <strong>Status Before:</strong>
            <div>
              <v-chip
                small
                :color="statusColor(report.list_status_before)"
                dark
                class="text-capitalize"
              >
                {{ report.list_status_before || 'N/A' }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Status After:</strong>
            <div>
              <v-chip
                small
                :color="statusColor(report.list_status_after)"
                dark
                class="text-capitalize"
              >
                {{ report.list_status_after || 'N/A' }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Project Status:</strong>
            <div>
              <v-chip small :color="statusColor(report.status)" dark class="text-capitalize">
                {{ report.status || 'N/A' }}
              </v-chip>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Error Code:</strong>
            <div>{{ report.error || 'N/A' }}</div>
          </v-col>

          <v-col cols="12">
            <strong>Purchase Needed:</strong>
            <div>{{ report.purchase_needed ? 'Yes' : 'No' }}</div>
          </v-col>
        </v-row>

        <!-- Inspection Findings -->
        <v-divider class="my-4"></v-divider>
        <h4 class="text-subtitle-1 font-weight-medium mb-2">Inspection Findings</h4>
        <v-list dense two-line>
          <v-list-item v-for="(finding, i) in findingsArray" :key="'f-' + i">
            <v-list-item-icon><v-icon small>mdi-checkbox-marked-circle</v-icon></v-list-item-icon>
            <v-list-item-content>{{ finding }}</v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Actions -->
        <v-divider class="my-4"></v-divider>
        <h4 class="text-subtitle-1 font-weight-medium mb-2">Actions</h4>
        <v-list dense>
          <v-list-item v-for="(a, i) in report.actions" :key="'a-' + i">
            <v-list-item-icon><v-icon small>mdi-wrench</v-icon></v-list-item-icon>
            <v-list-item-content>{{ a.action }}</v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Recommendations -->
        <v-divider class="my-4"></v-divider>
        <h4 class="text-subtitle-1 font-weight-medium mb-2">Recommendations</h4>
        <v-list dense>
          <v-list-item v-for="(rec, i) in recommendationsArray" :key="'r-' + i">
            <v-list-item-icon><v-icon small>mdi-lightbulb-on</v-icon></v-list-item-icon>
            <v-list-item-content>{{ rec }}</v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Components -->
        <v-divider class="my-4"></v-divider>
        <h4 class="text-subtitle-1 font-weight-medium mb-2">Machine Components</h4>
        <v-list dense>
          <v-list-item v-for="(comp, i) in report.machine_components" :key="'c-' + i">
            <v-list-item-icon><v-icon small>mdi-cog</v-icon></v-list-item-icon>
            <v-list-item-content>
              {{ comp.name }} ({{ comp.type?.name }} - {{ comp.category?.name }})
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, ErrorPopup, SuccessPopup } from '@/libs/utils'
import { isAxiosError } from 'axios'

export default {
  name: 'ShowDowntimeReport',
  data() {
    return {
      report: {},
    }
  },
  computed: {
    downtimeReportId() {
      return this.$route.params.downtimeReportId
    },
    findingsArray() {
      return this.report.inspection_findings
        ? this.report.inspection_findings.split(/\r\n|\n|\r/)
        : []
    },
    recommendationsArray() {
      return this.report.recommendations ? this.report.recommendations.split(/\r\n|\n|\r/) : []
    },
  },
  created() {
    this.fetchReport()
  },
  methods: {
    // existing methods...
    statusColor(status) {
      switch (status) {
        case 'pending':
          return 'orange lighten-2'
        case 'ongoing':
          return 'blue lighten-2'
        case 'completed':
          return 'green lighten-2'
        case 'cancelled':
          return 'red lighten-2'
        default:
          return 'grey lighten-2'
      }
    },

    async fetchReport() {
      try {
        LoadingPopup.fire('Loading', 'Fetching downtime report...')
        const axios = appAxios()
        const { data } = await axios.get(`/v3/downtime-reports/${this.downtimeReportId}`)
        this.report = data.data || {}
        LoadingPopup.close()
      } catch (e) {
        LoadingPopup.close()
        ErrorPopup.fire('Failed to load downtime report.')
      }
    },
    async printReport(id) {
      try {
        LoadingPopup.fire('Loading', 'Preparing Report...')
        const axios = appAxios()
        const { data } = await axios.get(`/v3/downtime-reports/${id}/pdf-v2`, {
          responseType: 'blob',
        })
        const fileURL = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        window.open(fileURL, '_blank')
        SuccessPopup.fire({
          title: 'Success',
          text: 'Successfully generated the PDF',
          toast: true,
          timer: 3000,
        })
      } catch (e) {
        ErrorPopup.fire('Error', e.message)
      }
    },
    formatDate(date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleString()
    },
  },
}
</script>

<style scoped>
.v-list-item {
  padding-top: 4px;
  padding-bottom: 4px;
}
</style>
